import axios from 'axios'

// ============================================================================
// API CONFIGURATION
// ============================================================================
const atarrayaApiUser = axios.create({
  baseURL: process.env.VUE_APP_ATARRAYA_API_URL + '/v3/identity',
  timeout: process.env.VUE_APP_ATARRAYA_TIMEOUT
})

// Request interceptor to add auth token
atarrayaApiUser.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
        Accept: 'application/json'
      }
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// ============================================================================
// CONSTANTS
// ============================================================================
const API_ENDPOINTS = {
  USERS: '/users',
  PROFILES: '/profiles',
  ROLES: '/roles',
  PERMISSIONS: '/permissions'
}

const QUERY_PARAMS = {
  WITH_PROFILE: '?with=profile',
  WITH_ROLE: '?with=role',
  WITH_PROFILE_AND_ROLE: '?with=profile&with=role'
}

// ============================================================================
// STATE
// ============================================================================
export const state = {
  users: [],
  simpleUsers: [],
  farmUsers: [],
  roles: [],
  permissions: []
}

// ============================================================================
// GETTERS
// ============================================================================
export const getters = {
  users: state => state.users,
  simpleUsers: state => state.simpleUsers,
  farmUsers: state => state.farmUsers,
  roles: state => state.roles,
  permissions: state => state.permissions,
  userById: state => id => {
    return state.users.find(user => user.id === id)
  },
  userByEmail: state => email => {
    return state.users.find(user => user.email === email)
  },
  hasUsers: state => state.users.length > 0,
  usersCount: state => state.users.length,
  activeUsers: state => {
    return state.users.filter(user => user.active === true)
  },
  farmUsersByStatus: state => status => {
    return state.farmUsers.filter(user => user.status === status)
  },
  activeFarmUsers: state => {
    return state.farmUsers.filter(user => user.status === 'active')
  },
  roleById: state => id => {
    return state.roles.find(role => role.id === id)
  }
}

// ============================================================================
// MUTATIONS
// ============================================================================
export const mutations = {
  LOAD_USERS(state, users) {
    state.users = users
  },

  LOAD_SIMPLE_USERS(state, users) {
    state.simpleUsers = users
  },

  LOAD_FARM_USERS(state, users) {
    state.farmUsers = users
  },

  ADD_USER(state, user) {
    state.users.push(user)
  },

  UPDATE_USER(state, updatedUser) {
    const index = state.users.findIndex(u => u.id === updatedUser.id)
    if (index !== -1) {
      state.users.splice(index, 1, { ...state.users[index], ...updatedUser })
    }
  },

  UPDATE_PROFILE(state, profileData) {
    const index = state.users.findIndex(u => u.id === profileData.user_id)
    if (index !== -1 && state.users[index].profile) {
      state.users[index].profile = { ...state.users[index].profile, ...profileData }
    }
  },

  DELETE_USER(state, userId) {
    state.users = state.users.filter(u => u.id !== userId)
  },

  LOAD_ROLES(state, roles) {
    state.roles = roles
  },

  LOAD_PERMISSIONS(state, permissions) {
    state.permissions = permissions
  },

  CLEAR_USERS(state) {
    state.users = []
    state.simpleUsers = []
    state.farmUsers = []
  },

  CLEAR_ROLES_PERMISSIONS(state) {
    state.roles = []
    state.permissions = []
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================
const handleApiError = (action, error) => {
  console.error(`User API ${action} error:`, error)
  throw error
}

const validateUserId = (userId) => {
  if (!userId) {
    throw new Error('User ID is required')
  }
}

// ============================================================================
// ACTIONS
// ============================================================================
export const actions = {
  async getUsers({ commit }) {
    try {
      const response = await atarrayaApiUser.get(
        `${API_ENDPOINTS.USERS}${QUERY_PARAMS.WITH_PROFILE_AND_ROLE}`
      )
      const users = response.data?.data || []
      commit('LOAD_USERS', users)
      return users
    } catch (error) {
      handleApiError('getUsers', error)
    }
  },

  async getSimpleUsers({ commit }) {
    try {
      const response = await atarrayaApiUser.get(API_ENDPOINTS.USERS)
      const users = response.data?.data || []
      commit('LOAD_SIMPLE_USERS', users)
      return users
    } catch (error) {
      handleApiError('getSimpleUsers', error)
    }
  },

  async getUsersWithRoles({ commit }) {
    try {
      const response = await atarrayaApiUser.get(
        `${API_ENDPOINTS.USERS}${QUERY_PARAMS.WITH_ROLE}`
      )
      const users = response.data?.data || []
      commit('LOAD_USERS', users)
      return users
    } catch (error) {
      handleApiError('getUsersWithRoles', error)
    }
  },

  async getUsersWithProfileAndRoles({ commit }) {
    try {
      const response = await atarrayaApiUser.get(
        `${API_ENDPOINTS.USERS}${QUERY_PARAMS.WITH_PROFILE_AND_ROLE}`
      )
      const users = response.data?.data || []
      commit('LOAD_USERS', users)
      return users
    } catch (error) {
      handleApiError('getUsersWithProfileAndRoles', error)
    }
  },

  async getUserById({ commit, getters }, userId) {
    try {
      validateUserId(userId)

      // Check if user exists in store first
      const existingUser = getters.userById(userId)
      if (existingUser) {
        return existingUser
      }

      // Fetch from API if not in store
      const response = await atarrayaApiUser.get(
        `${API_ENDPOINTS.USERS}/${userId}${QUERY_PARAMS.WITH_PROFILE}`
      )
      const user = response.data?.data
      if (user) {
        commit('ADD_USER', user)
      }
      return user
    } catch (error) {
      handleApiError('getUserById', error)
    }
  },

  async addUser({ commit }, userData) {
    try {
      const response = await atarrayaApiUser.post(API_ENDPOINTS.USERS, userData)
      const newUser = response.data?.data
      if (newUser) {
        commit('ADD_USER', newUser)
      }
      return newUser
    } catch (error) {
      handleApiError('addUser', error)
    }
  },

  async editUser({ commit }, userData) {
    try {
      validateUserId(userData.id)

      const response = await atarrayaApiUser.patch(
        `${API_ENDPOINTS.USERS}/${userData.id}`,
        userData
      )
      const updatedUser = response.data?.data || userData
      commit('UPDATE_USER', updatedUser)
      return updatedUser
    } catch (error) {
      handleApiError('editUser', error)
    }
  },

  async editUserProfile({ commit }, profileData) {
    try {
      validateUserId(profileData.user_id)

      const response = await atarrayaApiUser.patch(
        `${API_ENDPOINTS.USERS}/${profileData.user_id}${API_ENDPOINTS.PROFILES}`,
        profileData
      )
      const updatedProfile = response.data?.data || profileData
      commit('UPDATE_PROFILE', updatedProfile)
      return updatedProfile
    } catch (error) {
      handleApiError('editUserProfile', error)
    }
  },

  async deleteUser({ commit }, userId) {
    try {
      validateUserId(userId)

      await atarrayaApiUser.delete(`${API_ENDPOINTS.USERS}/${userId}`)
      commit('DELETE_USER', userId)
      return true
    } catch (error) {
      handleApiError('deleteUser', error)
    }
  },

  async getRoles({ commit }) {
    try {
      const response = await atarrayaApiUser.get(API_ENDPOINTS.ROLES)
      const roles = response.data?.data || []
      commit('LOAD_ROLES', roles)
      return roles
    } catch (error) {
      handleApiError('getRoles', error)
    }
  },

  async getPermissions({ commit }) {
    try {
      const response = await atarrayaApiUser.get(API_ENDPOINTS.PERMISSIONS)
      const permissions = response.data?.data || []
      commit('LOAD_PERMISSIONS', permissions)
      return permissions
    } catch (error) {
      handleApiError('getPermissions', error)
    }
  },

  async assignRole({ dispatch }, { userId, roleId }) {
    try {
      validateUserId(userId)

      const response = await atarrayaApiUser.post(
        `${API_ENDPOINTS.USERS}/${userId}${API_ENDPOINTS.ROLES}`,
        { role_id: roleId }
      )

      // Refresh user data
      await dispatch('getUserById', userId)

      return response.data?.data
    } catch (error) {
      handleApiError('assignRole', error)
    }
  },

  async getUsersByFarms({ commit }, farmIds) {
    try {
      if (!farmIds || !Array.isArray(farmIds) || farmIds.length === 0) {
        throw new Error('Farm IDs array is required')
      }

      const queryParams = farmIds.map(id => `farmId=${encodeURIComponent(id)}`).join('&')
      const response = await atarrayaApiUser.get(`${API_ENDPOINTS.USERS}?${queryParams}`)

      const users = response.data?.data || []
      commit('LOAD_FARM_USERS', users)
      return users
    } catch (error) {
      handleApiError('getUsersByFarms', error)
    }
  },

  clearUsersData({ commit }) {
    commit('CLEAR_USERS')
    commit('CLEAR_ROLES_PERMISSIONS')
  }
}